import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { getUserProfile, resolveUsername, getFollowing, getNotifications, updateNotificationStatus, markNotificationsRead, followUser, isFollowing, getFollowDoc, updateFollowGenres } from '../utils/socialFirestore';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import PageWrapperAuto from '../components/PageWrapperAuto';
import { useUserPrefs } from '../UserPrefsProvider';
import { FaQuestionCircle } from 'react-icons/fa';

export default function SocialPage() {
  const { user } = useAuth();
  const { textStyle } = useUI();
  const { prefs, labels } = useUserPrefs();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [lookupInput, setLookupInput] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [followedUsers, setFollowedUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [editingGenres, setEditingGenres] = useState(null); // { uid, displayName, genres, profileGenres, pickerGenres }
  const [followSearch, setFollowSearch] = useState('');
  const [batchSelections, setBatchSelections] = useState({}); // { notifId: Set of item indices }
  const [notice, setNotice] = useState(null);

  const showNotice = (text, tone = 'info') => setNotice({ text, tone });

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 2600);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      getUserProfile(user.uid),
      loadFollowedUsers(),
      loadNotifications()
    ]).then(([p]) => {
      setProfile(p);
      setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadFollowedUsers() {
    const uids = await getFollowing(user.uid);
    if (uids.length === 0) { setFollowedUsers([]); return; }
    const profiles = await Promise.all(
      uids.map(async (fuid) => {
        const [p, followDoc] = await Promise.all([
          getUserProfile(fuid),
          getFollowDoc(user.uid, fuid)
        ]);
        return p
          ? { uid: fuid, ...p, followGenres: followDoc?.genres || [] }
          : { uid: fuid, displayName: 'Unknown', username: '', followGenres: [] };
      })
    );
    setFollowedUsers(profiles);
  }

  async function loadNotifications() {
    const notifs = await getNotifications(user.uid);
    // Check if recipient follows each sender (we can read our own following list)
    const enriched = await Promise.all(
      notifs.map(async (n) => {
        const follows = await isFollowing(user.uid, n.senderUid);
        return { ...n, senderIsFollowed: follows };
      })
    );
    setNotifications(enriched);
    // Mark unread notifications as read (clears badge)
    const unread = notifs.filter(n => !n.readAt);
    if (unread.length > 0) {
      await markNotificationsRead(user.uid, unread.map(n => n.id));
    }
  }

  async function handleAccept(notif) {
    try {
      // Fetch the tune from sender's collection
      const tuneSnap = await getDoc(doc(db, 'users', notif.senderUid, 'tunes', notif.tuneId));
      if (!tuneSnap.exists()) {
        showNotice('This tune is no longer available.', 'error');
        await updateNotificationStatus(user.uid, notif.id, 'denied');
        setNotifications(prev => prev.filter(n => n.id !== notif.id));
        return;
      }
      const tuneData = tuneSnap.data();
      const sourceName = notif.senderDisplayName || notif.senderUsername || 'another musician';
      const sourceUsername = notif.senderUsername ? `@${notif.senderUsername}` : '';
      const sourceLabel = sourceUsername ? `${sourceName} (${sourceUsername})` : sourceName;

      // Import tune (exclude private fields)
      const newTuneId = crypto.randomUUID();
      await setDoc(doc(db, 'users', user.uid, 'tunes', newTuneId), {
        id: newTuneId,
        title: tuneData.title || '', type: tuneData.type || '', key: tuneData.key || '',
        genre: tuneData.genre || '', composer: tuneData.composer || '',
        abc: tuneData.abc || '', lyrics: tuneData.lyrics || '', chords: tuneData.chords || '',
        notes: tuneData.notes || '', mode: tuneData.mode || 'tunes',
        link1: tuneData.link1 || '', link2: tuneData.link2 || '', link3: tuneData.link3 || '',
        customTags: [], source: `Sent by ${sourceLabel}`,
        importedFrom: { uid: notif.senderUid, tuneId: notif.tuneId, displayName: sourceName, username: notif.senderUsername || '' },
        status: '', rating: '', sessions: [], sets: [], recordings: [], attachments: [],
        createdAt: serverTimestamp()
      });

      await updateNotificationStatus(user.uid, notif.id, 'accepted');
      setNotifications(prev => prev.map(n =>
        n.id === notif.id ? { ...n, status: 'accepted', acceptedId: newTuneId } : n
      ));
      window.dispatchEvent(new Event('notificationUpdate'));
    } catch (err) {
      console.error('Failed to accept tune:', err);
      showNotice('Failed to import tune. Please try again.', 'error');
    }
  }

  async function handleAcceptSet(notif) {
    try {
      // Fetch all tunes from sender's collection and import them
      const newTuneIds = [];
      for (const tuneId of (notif.tuneIds || [])) {
        const tuneSnap = await getDoc(doc(db, 'users', notif.senderUid, 'tunes', tuneId));
        if (!tuneSnap.exists()) continue;
        const tuneData = tuneSnap.data();
        const sourceName = notif.senderDisplayName || notif.senderUsername || 'another musician';
        const sourceUsername = notif.senderUsername ? `@${notif.senderUsername}` : '';
        const sourceLabel = sourceUsername ? `${sourceName} (${sourceUsername})` : sourceName;

        const newTuneId = crypto.randomUUID();
        await setDoc(doc(db, 'users', user.uid, 'tunes', newTuneId), {
          id: newTuneId,
          title: tuneData.title || '', type: tuneData.type || '', key: tuneData.key || '',
          genre: tuneData.genre || '', composer: tuneData.composer || '',
          abc: tuneData.abc || '', lyrics: tuneData.lyrics || '', chords: tuneData.chords || '',
          notes: tuneData.notes || '', mode: tuneData.mode || 'tunes',
          link1: tuneData.link1 || '', link2: tuneData.link2 || '', link3: tuneData.link3 || '',
          customTags: [], source: `Sent by ${sourceLabel}`,
          importedFrom: { uid: notif.senderUid, tuneId, displayName: sourceName, username: notif.senderUsername || '' },
          status: '', rating: '', sessions: [], sets: [], recordings: [], attachments: [],
          createdAt: serverTimestamp()
        });
        newTuneIds.push({ id: newTuneId, title: tuneData.title || '' });
      }

      // Create the set with references to newly imported tunes
      let acceptedSetId = null;
      if (newTuneIds.length > 0) {
        const sourceName = notif.senderDisplayName || notif.senderUsername || 'another musician';
        const sourceUsername = notif.senderUsername ? `@${notif.senderUsername}` : '';
        const sourceLabel = sourceUsername ? `${sourceName} (${sourceUsername})` : sourceName;
        // Fetch original set to get its mode and notes
        let setMode = 'tunes';
        let setNotes = '';
        try {
          const setSnap = await getDoc(doc(db, 'users', notif.senderUid, 'sets', notif.setId));
          if (setSnap.exists()) {
            const setData = setSnap.data();
            setMode = setData.mode || 'tunes';
            setNotes = setData.notes || '';
          }
        } catch {}
        const newSetId = crypto.randomUUID();
        await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), {
          id: newSetId,
          title: notif.setTitle || 'Untitled Set',
          tunes: newTuneIds.map(t => ({ id: t.id, title: t.title })),
          notes: setNotes, mode: setMode, source: `Sent by ${sourceLabel}`,
          importedFrom: { uid: notif.senderUid, setId: notif.setId, displayName: sourceName, username: notif.senderUsername || '' },
          customTags: [], rating: '', createdAt: serverTimestamp()
        });
        acceptedSetId = newSetId;
      }

      await updateNotificationStatus(user.uid, notif.id, 'accepted');
      setNotifications(prev => prev.map(n =>
        n.id === notif.id ? { ...n, status: 'accepted', acceptedId: acceptedSetId } : n
      ));
      window.dispatchEvent(new Event('notificationUpdate'));
    } catch (err) {
      console.error('Failed to accept set:', err);
      showNotice('Failed to import set. Please try again.', 'error');
    }
  }

  async function handleDeny(notif) {
    await updateNotificationStatus(user.uid, notif.id, 'denied');
    setNotifications(prev => prev.filter(n => n.id !== notif.id));
    window.dispatchEvent(new Event('notificationUpdate'));
  }

  async function handleFollowSender(notif) {
    try {
      await followUser(user.uid, notif.senderUid);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, senderIsFollowed: true } : n));
      // Refresh followed users list
      await loadFollowedUsers();
    } catch {
      // silently fail
    }
  }

  async function handleAcceptBatch(notif, mode) {
    try {
      const selected = mode === 'all'
        ? notif.items.map((_, i) => i)
        : Array.from(batchSelections[notif.id] || []);
      if (selected.length === 0) return;

      const sourceName = notif.senderDisplayName || notif.senderUsername || 'another musician';
      const sourceUsername = notif.senderUsername ? `@${notif.senderUsername}` : '';
      const sourceLabel = sourceUsername ? `${sourceName} (${sourceUsername})` : sourceName;
      const acceptedItems = [];

      for (const idx of selected) {
        const item = notif.items[idx];
        if (item.itemType === 'tune') {
          const tuneSnap = await getDoc(doc(db, 'users', notif.senderUid, 'tunes', item.tuneId));
          if (!tuneSnap.exists()) continue;
          const tuneData = tuneSnap.data();
          const newTuneId = crypto.randomUUID();
          await setDoc(doc(db, 'users', user.uid, 'tunes', newTuneId), {
            id: newTuneId,
            title: tuneData.title || '', type: tuneData.type || '', key: tuneData.key || '',
            genre: tuneData.genre || '', composer: tuneData.composer || '',
            abc: tuneData.abc || '', lyrics: tuneData.lyrics || '', chords: tuneData.chords || '',
            notes: tuneData.notes || '', mode: tuneData.mode || 'tunes',
            link1: tuneData.link1 || '', link2: tuneData.link2 || '', link3: tuneData.link3 || '',
            customTags: [], source: `Sent by ${sourceLabel}`,
            importedFrom: { uid: notif.senderUid, tuneId: item.tuneId, displayName: sourceName, username: notif.senderUsername || '' },
            status: '', rating: '', sessions: [], sets: [], recordings: [], attachments: [],
            createdAt: serverTimestamp()
          });
          acceptedItems.push({ ...item, acceptedId: newTuneId });
        } else if (item.itemType === 'set') {
          // Import tunes in this set
          const newTuneIds = [];
          for (const tuneId of (item.tuneIds || [])) {
            const tuneSnap = await getDoc(doc(db, 'users', notif.senderUid, 'tunes', tuneId));
            if (!tuneSnap.exists()) continue;
            const tuneData = tuneSnap.data();
            const newTuneId = crypto.randomUUID();
            await setDoc(doc(db, 'users', user.uid, 'tunes', newTuneId), {
              id: newTuneId,
              title: tuneData.title || '', type: tuneData.type || '', key: tuneData.key || '',
              genre: tuneData.genre || '', composer: tuneData.composer || '',
              abc: tuneData.abc || '', lyrics: tuneData.lyrics || '', chords: tuneData.chords || '',
              notes: tuneData.notes || '', mode: tuneData.mode || 'tunes',
              link1: tuneData.link1 || '', link2: tuneData.link2 || '', link3: tuneData.link3 || '',
              customTags: [], source: `Sent by ${sourceLabel}`,
              importedFrom: { uid: notif.senderUid, tuneId, displayName: sourceName, username: notif.senderUsername || '' },
              status: '', rating: '', sessions: [], sets: [], recordings: [], attachments: [],
              createdAt: serverTimestamp()
            });
            newTuneIds.push({ id: newTuneId, title: tuneData.title || '' });
          }
          if (newTuneIds.length > 0) {
            // Fetch original set to get its mode and notes
            let setMode = 'tunes';
            let setNotes = '';
            try {
              const setSnap = await getDoc(doc(db, 'users', notif.senderUid, 'sets', item.setId));
              if (setSnap.exists()) {
                const setData = setSnap.data();
                setMode = setData.mode || 'tunes';
                setNotes = setData.notes || '';
              }
            } catch {}
            const newSetId = crypto.randomUUID();
            await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), {
              id: newSetId,
              title: item.setTitle || 'Untitled Set',
              tunes: newTuneIds.map(t => ({ id: t.id, title: t.title })),
              notes: setNotes, mode: setMode, source: `Sent by ${sourceLabel}`,
              importedFrom: { uid: notif.senderUid, setId: item.setId, displayName: sourceName, username: notif.senderUsername || '' },
              customTags: [], rating: '', createdAt: serverTimestamp()
            });
            acceptedItems.push({ ...item, acceptedId: newSetId });
          }
        }
      }

      await updateNotificationStatus(user.uid, notif.id, 'accepted');
      setNotifications(prev => prev.map(n =>
        n.id === notif.id ? { ...n, status: 'accepted', acceptedItems } : n
      ));
      setBatchSelections(prev => { const next = { ...prev }; delete next[notif.id]; return next; });
      window.dispatchEvent(new Event('notificationUpdate'));
    } catch (err) {
      console.error('Failed to accept batch:', err);
      showNotice('Failed to import selected items. Please try again.', 'error');
    }
  }

  const myUsername = profile?.username || '';

  const copyUsername = () => {
    navigator.clipboard.writeText(myUsername).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleInvite = async () => {
    const url = window.location.origin;
    const senderName = profile?.displayName || 'A friend';
    const text = `${senderName} thought you'd like to try AllMyTunes — organize your ${labels.socialItemsLabel}, sets, and sessions in one place. Sign up free at ${url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Try AllMyTunes', text, url });
        return;
      } catch {
        // user cancelled or share failed — fall through to clipboard
      }
    }
    navigator.clipboard.writeText(text).then(() => {
      showNotice('Invite link copied to clipboard!');
    }).catch(() => {
      showNotice('Could not copy — please copy the link manually.', 'error');
    });
  };

  async function handleLookup() {
    const input = lookupInput.trim();
    if (!input) return;

    setLookupLoading(true);
    setLookupError('');

    try {
      const uid = await resolveUsername(input);
      if (uid) {
        if (uid === user?.uid) {
          setLookupError("That's you!");
        } else {
          navigate(`/user/${uid}`);
          return;
        }
      } else {
        setLookupError('No user found with that username.');
      }
    } catch {
      setLookupError('Something went wrong. Please try again.');
    }
    setLookupLoading(false);
  }

  if (loading) return <PageWrapperAuto><p style={{ textAlign: 'center' }}>Loading...</p></PageWrapperAuto>;

  return (
    <PageWrapperAuto>
      <div className="social-page" style={{ maxWidth: '500px', margin: '0 auto', padding: '20px 1rem 1rem', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.5rem' }}>
          <button
            className="btn-subtle search-control-btn"
            onClick={() => navigate(-1)}
            style={{ fontSize: 'var(--ui-compact-font)', padding: '5px 11px' }}
          >
            ← Back
          </button>
        </div>
        <div className="app-title ui-density-title" style={{ justifyContent: 'center' }}>
          <span>Social</span>
          <FaQuestionCircle
            onClick={() => navigate('/help#following-sharing')}
            style={{ fontSize: '0.65em', cursor: 'pointer', color: 'var(--text-link, #228be6)', opacity: 0.8 }}
            title="Help & Tutorials"
          />
        </div>
        {notice && (
          <div style={{
            margin: '0.5rem 0 0.75rem',
            padding: '8px 12px',
            borderRadius: '8px',
            textAlign: 'center',
            backgroundColor: notice.tone === 'error' ? '#5a1f1f' : '#1f3f5a',
            color: '#fff',
            fontSize: '0.88rem'
          }}>
            {notice.text}
          </div>
        )}

        <div style={{ marginTop: '1.5rem' }}>
          <button
            className="btn-subtle search-control-btn"
            onClick={() => navigate('/social/profile')}
            style={{ width: '100%', maxWidth: '280px', marginBottom: '0.75rem', padding: '10px 20px' }}
          >
            {profile ? 'Edit My Profile' : 'Set Up My Profile'}
          </button>
        </div>

        {/* My username section */}
        {myUsername ? (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ ...textStyle, marginBottom: '0.5rem' }}>Your username:</p>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span style={{
                ...textStyle,
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}>
                @{myUsername}
              </span>
              <button className="btn-subtle search-control-btn" onClick={copyUsername}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        ) : (
          <p style={{ ...textStyle, marginTop: '1rem', opacity: 0.7, fontSize: '0.85rem' }}>
            Set a username in your profile so others can find you.
          </p>
        )}

        {/* Invite a friend */}
        <div style={{ marginTop: '1.25rem' }}>
          <button
            className="btn-subtle search-control-btn"
            onClick={handleInvite}
            style={{ width: '100%', maxWidth: '280px', padding: '10px 20px' }}
          >
            Invite a Friend
          </button>
        </div>

        {/* Look up another user */}
        <div style={{ marginTop: '1.5rem' }}>
          <p style={{ ...textStyle, marginBottom: '0.5rem' }}>Find a user:</p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <input
              className="search-input"
              value={lookupInput}
              onChange={e => { setLookupInput(e.target.value); setLookupError(''); }}
              placeholder="Enter a username"
              style={{
                gridColumn: 'auto',
                flex: '1 1 200px',
                maxWidth: '300px',
              }}
              onKeyDown={e => e.key === 'Enter' && handleLookup()}
            />
            <button className="btn-subtle search-control-btn" onClick={handleLookup} disabled={lookupLoading}>
              {lookupLoading ? '...' : 'Go'}
            </button>
          </div>
          {lookupError && (
            <p style={{ ...textStyle, fontSize: '0.8rem', color: '#e44', marginTop: '4px' }}>{lookupError}</p>
          )}
        </div>

        {/* Notifications */}
        {notifications.length > 0 && (
          <div style={{ marginTop: '1.5rem', textAlign: 'left' }}>
            <h4 style={{ ...textStyle, textAlign: 'center', marginBottom: '0.5rem' }}>
              Notifications ({notifications.length})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {notifications.map(notif => (
                <div
                  key={notif.id}
                  style={{
                    padding: '12px',
                    backgroundColor: 'var(--input-bg)',
                    borderRadius: '8px'
                  }}
                >
                  {/* Header: who sent what */}
                  <p style={{ ...textStyle, margin: '0 0 6px 0' }}>
                    <strong>{notif.senderDisplayName || notif.senderUsername || 'Someone'}</strong>
                    {!notif.senderIsFollowed ? ' wants to send you ' : ' sent you '}
                    {notif.type === 'set_send' ? (
                      <>a set: <strong>{notif.setTitle}</strong> ({(notif.tuneIds || []).length} tune{(notif.tuneIds || []).length !== 1 ? 's' : ''})</>
                    ) : notif.type === 'batch_send' ? (
                      <>{(notif.items || []).length} item{(notif.items || []).length !== 1 ? 's' : ''}</>
                    ) : (
                      <>a tune: <strong>{notif.tuneTitle}</strong></>
                    )}
                  </p>

                  {!notif.senderIsFollowed && (
                    <p style={{ ...textStyle, fontSize: '0.8rem', opacity: 0.7, margin: '0 0 6px 0' }}>
                      You don't follow this person yet.{' '}
                      <button
                        className="btn-subtle search-control-btn"
                        onClick={() => handleFollowSender(notif)}
                        style={{ padding: '2px 8px', fontSize: '0.8rem' }}
                      >
                        Follow
                      </button>
                    </p>
                  )}

                  {/* --- batch_send type --- */}
                  {notif.type === 'batch_send' ? (
                    notif.status === 'accepted' ? (
                      <div>
                        <p style={{ ...textStyle, fontSize: '0.85rem', color: '#2a2', margin: '0 0 6px 0' }}>
                          Accepted!
                        </p>
                        {(notif.acceptedItems || []).map((item, i) => (
                          <button
                            key={i}
                            className="btn-subtle search-control-btn"
                            onClick={() => navigate(item.itemType === 'set' ? `/sets/${item.acceptedId}` : `/tune/${item.acceptedId}`)}
                            style={{ padding: '4px 10px', fontSize: '0.8rem', marginRight: '6px', marginBottom: '4px', backgroundColor: '#2a2', color: '#fff' }}
                          >
                            {item.itemType === 'set' ? item.setTitle : item.tuneTitle} — View
                          </button>
                        ))}
                        <div style={{ marginTop: '6px' }}>
                          <button className="btn-subtle search-control-btn" onClick={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))} style={{ padding: '6px 14px' }}>
                            Dismiss
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {/* Item checklist */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', margin: '6px 0' }}>
                          {(notif.items || []).map((item, idx) => {
                            const sel = batchSelections[notif.id] || new Set(notif.items.map((_, i) => i));
                            return (
                              <label key={idx} style={{
                                ...textStyle, display: 'flex', alignItems: 'center', gap: '6px',
                                fontSize: '0.85rem', cursor: 'pointer', padding: '4px 6px',
                                backgroundColor: 'var(--bg-primary)', borderRadius: '6px'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={sel.has(idx)}
                                  onChange={() => {
                                    setBatchSelections(prev => {
                                      const current = prev[notif.id] || new Set(notif.items.map((_, i) => i));
                                      const next = new Set(current);
                                      if (next.has(idx)) next.delete(idx);
                                      else next.add(idx);
                                      return { ...prev, [notif.id]: next };
                                    });
                                  }}
                                />
                                {item.itemType === 'set' ? '📁 ' : '🎵 '}
                                {item.itemType === 'set' ? item.setTitle : item.tuneTitle}
                                {item.itemType === 'set' && item.tuneIds?.length > 0 && (
                                  <span style={{ opacity: 0.6, fontSize: '0.75rem' }}>
                                    ({item.tuneIds.length} tune{item.tuneIds.length !== 1 ? 's' : ''})
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button className="btn-subtle search-control-btn" onClick={() => handleAcceptBatch(notif, 'all')} style={{ padding: '6px 14px' }}>
                            Accept All
                          </button>
                          <button
                            className="btn-subtle search-control-btn"
                            onClick={() => handleAcceptBatch(notif, 'selected')}
                            style={{ padding: '6px 14px' }}
                            disabled={!(batchSelections[notif.id] || new Set(notif.items.map((_, i) => i))).size}
                          >
                            Accept Selected
                          </button>
                          <button className="btn-subtle search-control-btn" onClick={() => handleDeny(notif)} style={{ padding: '6px 14px' }}>
                            Deny All
                          </button>
                        </div>
                      </div>
                    )
                  ) : (
                    /* --- tune_send / set_send types --- */
                    notif.status === 'accepted' ? (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button
                          className="btn-subtle search-control-btn"
                          onClick={() => navigate(notif.type === 'set_send' ? `/sets/${notif.acceptedId}` : `/tune/${notif.acceptedId}`)}
                          style={{ padding: '6px 14px', backgroundColor: '#2a2', color: '#fff' }}
                        >
                          {notif.type === 'set_send' ? 'Set received' : 'Tune received'} — View
                        </button>
                        <button className="btn-subtle search-control-btn" onClick={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))} style={{ padding: '6px 14px' }}>
                          Dismiss
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button className="btn-subtle search-control-btn" onClick={() => notif.type === 'set_send' ? handleAcceptSet(notif) : handleAccept(notif)} style={{ padding: '6px 14px' }}>
                          Accept
                        </button>
                        <button className="btn-subtle search-control-btn" onClick={() => handleDeny(notif)} style={{ padding: '6px 14px' }}>
                          Deny
                        </button>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {notifications.length === 0 && (
          <p style={{ ...textStyle, marginTop: '1.5rem', opacity: 0.75 }}>
            No new notifications yet. Ask a friend to send you a tune or set.
          </p>
        )}

        {/* Following list */}
        {followedUsers.length > 0 && (
          <div style={{ marginTop: '1.5rem', textAlign: 'left' }}>
            <h4 style={{ ...textStyle, textAlign: 'center', marginBottom: '0.5rem' }}>Following ({followedUsers.length})</h4>
            {prefs?.showFieldHints && (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', margin: '0 auto 0.5rem', maxWidth: '400px' }}>
                You're following the <strong>allmytunes</strong> account &mdash; tap it to browse and import curated sampler sets.
                <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', opacity: 0.7 }}>
                  <span onClick={() => navigate('/help')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Toggle hints in Help &amp; Tutorials</span>
                </div>
              </div>
            )}
            {followedUsers.length > 3 && (
              <input
                className="search-input"
                value={followSearch}
                onChange={e => setFollowSearch(e.target.value)}
                placeholder="Search contacts..."
                style={{
                  gridColumn: 'auto',
                  width: '100%',
                  marginBottom: '0.5rem',
                  boxSizing: 'border-box'
                }}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {followedUsers.filter(u => {
                if (!followSearch.trim()) return true;
                const q = followSearch.toLowerCase();
                return (u.displayName || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q);
              }).map(u => (
                <div
                  key={u.uid}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: 'var(--input-bg)',
                    borderRadius: '8px',
                  }}
                >
                  <div
                    onClick={() => navigate(`/user/${u.uid}`)}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    {u.photoURL && (
                      <img
                        src={u.photoURL}
                        alt=""
                        style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <strong style={textStyle}>{u.displayName || 'Musician'}</strong>
                      {u.username && (
                        <span style={{ ...textStyle, opacity: 0.6, marginLeft: '6px', fontSize: '0.85em' }}>
                          @{u.username}
                        </span>
                      )}
                    </div>
                  </div>
                  {(u.genres?.length > 0 || u.followGenres?.length > 0) && (
                    <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                      <span style={{ ...textStyle, fontSize: '0.75rem', opacity: 0.5 }}>
                        {u.followGenres?.length > 0 ? 'Following:' : 'All genres'}
                      </span>
                      {u.followGenres?.length > 0 && u.followGenres.map(g => (
                        <span key={g} style={{
                          ...textStyle, fontSize: '0.7rem', padding: '1px 6px',
                          backgroundColor: 'var(--input-bg)', borderRadius: '8px',
                          border: '1px solid var(--card-border)'
                        }}>{g}</span>
                      ))}
                      {u.genres?.length > 0 && (
                        <button
                          className="btn-subtle search-control-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingGenres({
                              uid: u.uid,
                              displayName: u.displayName || 'Musician',
                              genres: u.followGenres || [],
                              profileGenres: u.genres || [],
                              pickerGenres: u.followGenres?.length > 0 ? [...u.followGenres] : [...(u.genres || [])]
                            });
                          }}
                          style={{ padding: '1px 8px', fontSize: '0.7rem' }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {followedUsers.length === 0 && (
          <p style={{ ...textStyle, marginTop: '1rem', opacity: 0.75 }}>
            You are not following anyone yet. Use the username search above to find {labels.socialUserLabel}.
          </p>
        )}

        {profile && profile.visibility === 'private' && (
          <p style={{ ...textStyle, marginTop: '1.5rem', opacity: 0.7 }}>
            Your profile is currently private. Edit your profile to make it public so others can see your {labels.socialItemsLabel}.
          </p>
        )}

        {!profile && (
          <p style={{ ...textStyle, marginTop: '1.5rem', opacity: 0.7 }}>
            Set up your profile to share your {labels.socialItemsLabel} with other {labels.socialUserLabel}.
          </p>
        )}
      </div>

      {/* Genre edit overlay */}
      {editingGenres && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '1rem'
        }}
          onClick={() => setEditingGenres(null)}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-primary, #fff)',
              borderRadius: '12px',
              padding: '1.5rem',
              maxWidth: '360px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ ...textStyle, marginTop: 0, textAlign: 'center' }}>
              Genres from {editingGenres.displayName}
            </h3>
            <p style={{ ...textStyle, fontSize: '0.85rem', opacity: 0.7, textAlign: 'center', marginBottom: '1rem' }}>
              Select which genres you want to see:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1rem' }}>
              {editingGenres.profileGenres.map(genre => (
                <label key={genre} style={{
                  ...textStyle,
                  display: 'flex', alignItems: 'center', gap: '8px',
                  cursor: 'pointer', padding: '6px 8px',
                  backgroundColor: 'var(--input-bg)', borderRadius: '8px'
                }}>
                  <input
                    type="checkbox"
                    checked={editingGenres.pickerGenres.includes(genre)}
                    onChange={() => {
                      setEditingGenres(prev => ({
                        ...prev,
                        pickerGenres: prev.pickerGenres.includes(genre)
                          ? prev.pickerGenres.filter(g => g !== genre)
                          : [...prev.pickerGenres, genre]
                      }));
                    }}
                  />
                  {genre}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button
                className="btn-subtle search-control-btn"
                onClick={async () => {
                  const allSelected = editingGenres.pickerGenres.length === editingGenres.profileGenres.length;
                  const genres = allSelected ? [] : editingGenres.pickerGenres;
                  await updateFollowGenres(user.uid, editingGenres.uid, genres);
                  setEditingGenres(null);
                  await loadFollowedUsers();
                }}
                disabled={editingGenres.pickerGenres.length === 0}
                style={{ padding: '8px 20px' }}
              >
                Save
              </button>
              <button
                className="btn-subtle search-control-btn"
                onClick={() => setEditingGenres(null)}
                style={{ padding: '8px 20px' }}
              >
                Cancel
              </button>
            </div>
            {editingGenres.pickerGenres.length === 0 && (
              <p style={{ ...textStyle, fontSize: '0.8rem', color: '#e44', textAlign: 'center', marginTop: '6px' }}>
                Select at least one genre.
              </p>
            )}
          </div>
        </div>
      )}
    </PageWrapperAuto>
  );
}
